<template>
  <article class="project">
    <div v-if="image" class="project__thumb">
      <img
        class="project__img"
        :src="image"
        :alt="`${title} preview`"
        loading="lazy"
      />
    </div>

    <div class="project__body">
      <header class="project__head">
        <div class="project__titles">
          <h3 class="project__title">
            <router-link
              v-if="showDetailLink"
              class="project__title-link"
              :to="{ name: 'Project', params: { slug } }"
            >
              {{ title }}
            </router-link>
            <template v-else>{{ title }}</template>
          </h3>
          <span v-if="status" class="project__status">{{ status }}</span>
        </div>
        <div class="project__links">
          <a
            v-if="code"
            class="project__link"
            :href="code"
            target="_blank"
            rel="noopener"
          >
            Code
          </a>
          <a
            v-if="website"
            class="project__link"
            :href="website"
            target="_blank"
            rel="noopener"
          >
            Website
          </a>
        </div>
      </header>
      <p class="project__desc">{{ description }}</p>
      <ul v-if="tech_stack && tech_stack.length" class="project__stack">
        <li v-for="(tech, i) in tech_stack" :key="i">{{ tech }}</li>
      </ul>
    </div>
  </article>
</template>

<script>
import { projectDetailPagesEnabled } from '@/data/featuredProjects'

export default {
  name: 'Projects',
  props: {
    title: String,
    description: String,
    tech_stack: Array,
    code: String,
    website: String,
    image: String,
    status: String,
    slug: String,
    featured: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    showDetailLink () {
      return projectDetailPagesEnabled && Boolean(this.slug)
    }
  }
}
</script>

<style scoped>
.project {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.project__thumb {
  flex-shrink: 0;
  width: 80px;
}

.project__img {
  display: block;
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 5px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.45);
}

.project__body {
  flex: 1;
  min-width: 0;
}

.project__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem 1rem;
  margin-bottom: 0.4rem;
}

.project__titles {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.45rem 0.65rem;
}

.project__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: var(--ink);
}

.project__title-link {
  color: var(--accent);
  text-decoration: none;
  border-bottom: 1.5px solid currentColor;
  transition: color 0.2s var(--ease), border-color 0.2s var(--ease);
}

.project__title-link:hover {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.project__title-link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
}

.project__status {
  font-family: var(--font-mono);
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--accent);
  border: 1px solid var(--accent);
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  line-height: 1.2;
}

.project__links {
  display: flex;
  gap: 0.85rem;
}

.project__link {
  font-family: var(--font-mono);
  font-size: 0.8rem;
  font-weight: 500;
  text-decoration: none;
  color: var(--accent);
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s var(--ease), color 0.2s var(--ease);
}

.project__link:hover {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.project__desc {
  margin: 0;
  color: var(--ink-muted);
  line-height: 1.55;
}

.project__stack {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  list-style: none;
  margin: 0.85rem 0 0;
  padding: 0;
}

.project__stack li {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  color: var(--ink-muted);
  padding: 0.2rem 0.55rem;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.45);
}

@media (max-width: 480px) {
  .project {
    gap: 0.85rem;
  }

  .project__thumb,
  .project__img {
    width: 64px;
    height: 64px;
  }
}
</style>
